import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { tokenStorage } from '@/utils/jwt';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Statuses the caller handles itself (e.g. 404 = "not set up yet") — no error toast. */
    expectedStatuses?: number[];
  }
}

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE,
  timeout: 30000,
});

api.interceptors.request.use((cfg) => {
  const t = tokenStorage.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Older backends (before ACCOUNT_NOT_CONNECTED existed) only send the words.
const NOT_CONNECTED_TEXT = /^No (active |default )?(Meta|TikTok|WhatsApp)\b[^.]*\b(configured|connected)\b/i;

/** "Nothing connected yet" — pages show a connect state for it, so a read never raises a toast. */
export const isNotConnectedError = (status?: number, body?: { code?: string; message?: string }) =>
  body?.code === 'ACCOUNT_NOT_CONNECTED' || (status === 404 && !!body?.message && NOT_CONNECTED_TEXT.test(body.message));

// One toast per message: a page firing five requests that fail the same way shows it once.
const errorToast = (message: string) => toast.error(message, { id: message });

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ ok?: boolean; code?: string; message?: string }>) => {
    const status = err.response?.status;
    const body = err.response?.data;
    if (status === 401) {
      tokenStorage.clear();
      window.location.replace('/login');
      return Promise.reject(err);
    }
    if (status === 403) {
      const code = body?.code;
      // Terminal states — kick the user out to a dedicated screen.
      if (code === 'SUBSCRIPTION_EXPIRED' || code === 'SUBSCRIPTION_SUSPENDED') {
        if (window.location.pathname !== '/subscription-blocked') {
          window.location.replace('/subscription-blocked');
        }
        return Promise.reject(err);
      }
      if (code === 'COMPANY_INACTIVE' || code === 'AUTH_ACCOUNT_SUSPENDED') {
        tokenStorage.clear();
        window.location.replace('/login');
        return Promise.reject(err);
      }
      // Everything else (role denial, plan-limit denial, etc.) — surface as
      // a toast in-place so the user keeps their context. Avoid redirecting
      // to /403 which is misleading: they ARE allowed in the area, they just
      // can't perform this specific action.
      errorToast(body?.message ?? 'You do not have permission to do that.');
      return Promise.reject(err);
    }
    if (status && err.config?.expectedStatuses?.includes(status)) return Promise.reject(err);
    const method = (err.config?.method ?? 'get').toLowerCase();
    if (isNotConnectedError(status, body) && method === 'get') return Promise.reject(err);
    if (body?.message) errorToast(body.message);
    else if (err.message) errorToast(err.message);
    return Promise.reject(err);
  },
);
